// client/src/components/pages/OrderManagement/Drivers/index.tsx (converted from .js, ST-00216)

import { useState, useEffect, useCallback, type ComponentType, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Plus,
  Search,
  X,
  Check,
  Loader,
  Edit2,
  Trash2,
  ArrowLeft,
  Shield,
  ShieldOff,
  Truck,
} from 'lucide-react';
import orderService from '../../../../services/orderService';
import { TableSkeleton, EmptyState, haulierChoices } from '../shared';
import { apiErrorReason, ORDERS_MANAGE } from '../shared/orderHelpers';
import { useAuth } from '../../../../types/commonComponents';
import ThemedSelectJs from '../../../common/ThemedSelect';
import { ThemedSelect, type ThemedSelectProps } from '../../../../types/commonComponents';
import type {
  CompanyId,
  DriverRow,
  FleetStats,
  OrderCompany,
} from '../../../../types/clientPages/orderManagement';

/**
 * ThemedSelect over company ids: it matches the value with String() and emits the chosen
 * option's own value, which is the numeric company_id (or '' for the empty option).
 */
const CompanySelect = ThemedSelectJs as unknown as ComponentType<
  Omit<ThemedSelectProps, 'value' | 'onChange'> & {
    value: CompanyId | '';
    onChange: (e: { target: { name?: string; value: CompanyId | '' } }) => void;
  }
>;

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', color: 'emerald' },
  { value: 'suspended', label: 'Suspended', color: 'amber' },
  { value: 'blacklisted', label: 'Blacklisted', color: 'red' },
] as const;

const statusBadgeClasses: Record<string, string | undefined> & { active: string } = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  suspended: 'bg-amber-50 text-amber-700 border-amber-200',
  blacklisted: 'bg-red-50 text-red-700 border-red-200',
};

const StatusBadge = ({ status }: { status: string }) => {
  const opt = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  const cls = statusBadgeClasses[status] || statusBadgeClasses.active;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border ${cls}`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${status === 'active' ? 'bg-emerald-500' : status === 'suspended' ? 'bg-amber-500' : 'bg-red-500'}`}
      />
      {opt.label}
    </span>
  );
};

const WhitelistToggle = ({
  enabled,
  onClick,
}: {
  enabled: boolean;
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
}) => (
  <button
    onClick={onClick}
    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ${enabled ? 'bg-teal-500' : 'bg-slate-200'}`}
  >
    <span
      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-surface shadow-sm transition-transform duration-200 ${enabled ? 'translate-x-[18px]' : 'translate-x-[3px]'}`}
    />
  </button>
);

const MONO = { fontFamily: 'IBM Plex Mono, monospace' };
const SANS = { fontFamily: 'DM Sans, sans-serif' };

/* ──────────────────────────────────────────────────────
   Driver Modal
   ────────────────────────────────────────────────────── */
interface DriverForm {
  driver_name: string;
  license_number: string;
  contact_number: string;
  hauler_company_id: CompanyId | '';
  whitelisted: boolean;
  status: string;
  notes: string;
}

type DriverFormErrors = Partial<Record<keyof DriverForm, string>>;

interface DriverModalProps {
  driver: DriverRow | null;
  companies: OrderCompany[];
  onSave: () => void;
  onClose: () => void;
}

const DriverModal = ({ driver, companies, onSave, onClose }: DriverModalProps) => {
  const [form, setForm] = useState<DriverForm>({
    driver_name: driver?.driver_name || '',
    license_number: driver?.license_number || '',
    contact_number: driver?.contact_number || '',
    hauler_company_id: driver?.hauler_company_id || '',
    whitelisted: driver?.whitelisted ?? false,
    status: driver?.status || 'active',
    notes: driver?.notes || '',
  });
  const [errors, setErrors] = useState<DriverFormErrors>({});
  const [saving, setSaving] = useState(false);

  const set = <K extends keyof DriverForm>(field: K, value: DriverForm[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async () => {
    const errs: DriverFormErrors = {};
    if (!form.driver_name.trim()) errs.driver_name = 'Driver name is required';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    try {
      setSaving(true);
      const payload = { ...form, hauler_company_id: form.hauler_company_id || null };
      if (driver) {
        await orderService.updateDriver(driver.driver_id, payload);
      } else {
        await orderService.createDriver(payload);
      }
      onSave();
    } catch (err) {
      alert('Failed to save driver: ' + apiErrorReason(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200"
        onClick={(e) => e.stopPropagation()}
        style={SANS}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-500 flex items-center justify-center">
              <Users className="w-4.5 h-4.5 text-white" />
            </div>
            <h3 className="text-lg font-bold text-slate-800">
              {driver ? 'Edit Driver' : 'Add Driver'}
            </h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Driver Name */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-1.5">
              Driver Name <span className="text-red-400">*</span>
            </label>
            <input
              value={form.driver_name}
              onChange={(e) => set('driver_name', e.target.value)}
              placeholder="Full name"
              className={`w-full px-3.5 py-2.5 border rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500 transition-colors ${errors.driver_name ? 'border-red-300 bg-red-50/30' : 'border-slate-200'}`}
            />
            {errors.driver_name && (
              <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.driver_name}</p>
            )}
          </div>

          {/* License + Contact */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-1.5">
                License Number
              </label>
              <input
                value={form.license_number}
                onChange={(e) => set('license_number', e.target.value)}
                placeholder="e.g. DL-12345"
                className="w-full px-3.5 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500 transition-colors"
                style={MONO}
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-1.5">
                Contact Number
              </label>
              <input
                value={form.contact_number}
                onChange={(e) => set('contact_number', e.target.value)}
                placeholder="+27 ..."
                className="w-full px-3.5 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500 transition-colors"
              />
            </div>
          </div>

          {/* Haulier + Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-1.5">
                Haulier
              </label>
              <CompanySelect
                value={form.hauler_company_id}
                onChange={(e) => set('hauler_company_id', e.target.value)}
                className="w-full px-3.5 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500 transition-colors bg-surface"
              >
                <option value="">-- None --</option>
                {haulierChoices(companies, form.hauler_company_id, driver?.hauler_name).map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </CompanySelect>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-1.5">
                Status
              </label>
              <ThemedSelect
                value={form.status}
                onChange={(e) => set('status', e.target.value)}
                className="w-full px-3.5 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500 transition-colors bg-surface"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </ThemedSelect>
            </div>
          </div>

          {/* Whitelisted Toggle */}
          <div className="flex items-center justify-between py-3 px-4 bg-slate-50 rounded-lg border border-slate-100">
            <div>
              <label className="text-sm font-semibold text-slate-700">Whitelisted</label>
              <p className="text-xs text-slate-400 mt-0.5">
                Allow this driver on edge weighbridges
              </p>
            </div>
            <button
              type="button"
              onClick={() => set('whitelisted', !form.whitelisted)}
              className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${form.whitelisted ? 'bg-teal-500' : 'bg-slate-200'}`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-surface rounded-full shadow transition-transform duration-200 ${form.whitelisted ? 'translate-x-5' : ''}`}
              />
            </button>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-1.5">
              Notes
            </label>
            <textarea
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              rows={3}
              placeholder="Optional notes about this driver..."
              className="w-full px-3.5 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500 transition-colors resize-none"
            />
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                {driver ? 'Update' : 'Create'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────
   Confirm Dialog
   ────────────────────────────────────────────────────── */
const ConfirmDialog = ({
  message,
  onConfirm,
  onCancel,
}: {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}) => (
  <div
    className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    onClick={onCancel}
  >
    <div
      className="bg-surface rounded-2xl shadow-2xl w-full max-w-sm border border-slate-200"
      onClick={(e) => e.stopPropagation()}
      style={SANS}
    >
      <div className="p-6">
        <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mx-auto mb-4">
          <Trash2 className="w-5 h-5 text-red-500" />
        </div>
        <h4 className="text-base font-bold text-slate-800 text-center mb-2">Confirm Delete</h4>
        <p className="text-sm text-slate-500 text-center">{message}</p>
      </div>
      <div className="flex gap-3 px-6 pb-6">
        <button
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-600 transition-colors"
        >
          Delete
        </button>
      </div>
    </div>
  </div>
);

/* ──────────────────────────────────────────────────────
   Main Component
   ────────────────────────────────────────────────────── */
export default function Drivers() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  // Writes need orders.manage on the server (ST-00227 / ORD-12); without it the page is read-only.
  const canManage = hasPermission(ORDERS_MANAGE);
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState<DriverRow[]>([]);
  const [filtered, setFiltered] = useState<DriverRow[]>([]);
  const [companies, setCompanies] = useState<OrderCompany[]>([]);
  const [stats, setStats] = useState<FleetStats | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<DriverRow | null>(null);
  const [deleting, setDeleting] = useState<DriverRow | null>(null);
  const [selected, setSelected] = useState<DriverRow | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [dData, cData, sData] = await Promise.all([
        orderService.getDrivers(),
        orderService.getCompanies(),
        orderService.getDriverStats(),
      ]);
      setDrivers(dData.drivers || []);
      setFiltered(dData.drivers || []);
      setCompanies(cData.companies || []);
      setStats(sData.stats || null);
    } catch (e) {
      console.error('Failed to load driver data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let f = drivers;
    if (search) {
      const q = search.toLowerCase();
      f = f.filter(
        (d) =>
          d.driver_name?.toLowerCase().includes(q) ||
          d.license_number?.toLowerCase().includes(q) ||
          d.hauler_name?.toLowerCase().includes(q)
      );
    }
    if (statusFilter) {
      f = f.filter((d) => d.status === statusFilter);
    }
    setFiltered(f);
  }, [drivers, search, statusFilter]);

  // The list rows carry no vehicle associations — fetch the detail record so the
  // panel can show "Assigned Vehicles". Keep the row data if that call fails.
  const loadDetail = async (driverId: string) => {
    try {
      const res = await orderService.getDriver(driverId);
      const detail = res.driver;
      if (detail) {
        setSelected((prev) => (prev?.driver_id === driverId ? detail : prev));
      }
    } catch (err) {
      console.error('Failed to fetch driver detail:', err);
    }
  };

  const handleSelectRow = (driver: DriverRow) => {
    setSelected(driver);
    loadDetail(driver.driver_id);
  };

  const handleWhitelistToggle = async (e: MouseEvent<HTMLButtonElement>, driver: DriverRow) => {
    e.stopPropagation();
    const newVal = !driver.whitelisted;
    try {
      await orderService.updateDriver(driver.driver_id, { whitelisted: newVal });
      // Update in place: a full reload swaps the table for a skeleton and loses
      // the scroll position in a list of thousands of drivers.
      setDrivers((prev) =>
        prev.map((d) => (d.driver_id === driver.driver_id ? { ...d, whitelisted: newVal } : d))
      );
      setSelected((prev) =>
        prev?.driver_id === driver.driver_id ? { ...prev, whitelisted: newVal } : prev
      );
      setStats((prev) =>
        prev ? { ...prev, whitelisted: Number(prev.whitelisted || 0) + (newVal ? 1 : -1) } : prev
      );
    } catch (err) {
      console.error('Failed to toggle whitelist:', err);
      alert('Failed to update whitelist status: ' + apiErrorReason(err));
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await orderService.deleteDriver(deleting.driver_id);
      if (selected?.driver_id === deleting.driver_id) setSelected(null);
      load();
    } catch (err) {
      alert('Failed to delete driver: ' + apiErrorReason(err));
    } finally {
      setDeleting(null);
    }
  };

  const handleSave = async () => {
    const editedId = editing?.driver_id;
    setShowModal(false);
    setEditing(null);
    await load();
    // Keep the detail panel in step with what was just saved.
    if (editedId && selected?.driver_id === editedId) loadDetail(editedId);
  };

  const statCards = [
    {
      label: 'Total',
      value: stats?.total,
      borderColor: 'border-l-slate-400',
      textColor: 'text-slate-700',
    },
    {
      label: 'Whitelisted',
      value: stats?.whitelisted,
      borderColor: 'border-l-teal-500',
      textColor: 'text-teal-700',
    },
    {
      label: 'Active',
      value: stats?.active,
      borderColor: 'border-l-emerald-500',
      textColor: 'text-emerald-700',
    },
    {
      label: 'Suspended',
      value: stats?.suspended,
      borderColor: 'border-l-amber-500',
      textColor: 'text-amber-700',
    },
    {
      label: 'Blacklisted',
      value: stats?.blacklisted,
      borderColor: 'border-l-red-500',
      textColor: 'text-red-700',
    },
  ];

  const filterPills = [
    { key: '', label: 'All' },
    ...STATUS_OPTIONS.map((s) => ({ key: s.value, label: s.label })),
  ];

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto min-h-screen bg-slate-50/50" style={SANS}>
      {/* Back Navigation */}
      <button
        onClick={() => navigate('/settings/modules/order-management/master-data')}
        className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-600 font-medium mb-5 transition-colors group"
      >
        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
        Master Data
      </button>

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-lg bg-amber-500 flex items-center justify-center shadow-sm">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-slate-800">Drivers</h1>
              {stats && (
                <span
                  className="px-2.5 py-0.5 bg-slate-100 text-slate-500 text-xs font-semibold rounded-full"
                  style={MONO}
                >
                  {stats.total}
                </span>
              )}
            </div>
            <p className="text-sm text-slate-400 mt-0.5">Driver registry and whitelisting</p>
          </div>
        </div>
        {canManage && (
          <button
            onClick={() => {
              setEditing(null);
              setShowModal(true);
            }}
            className="px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center text-sm font-semibold shadow-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Driver
          </button>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {statCards.map((card) => (
            <div
              key={card.label}
              className={`bg-surface rounded-lg border border-slate-200 border-l-[3px] ${card.borderColor} px-4 py-3`}
            >
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                {card.label}
              </p>
              <p className={`text-2xl font-bold mt-0.5 ${card.textColor}`} style={MONO}>
                {card.value || 0}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Filter Bar */}
      <div className="bg-surface rounded-xl border border-slate-200 px-4 py-3 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, license or haulier..."
              className="w-full pl-10 pr-8 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500 text-sm text-slate-700 placeholder-slate-400 transition-colors"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 hover:bg-slate-100 rounded"
              >
                <X className="w-3.5 h-3.5 text-slate-400" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {filterPills.map((pill) => {
              const isActive = statusFilter === pill.key;
              return (
                <button
                  key={pill.key}
                  onClick={() => setStatusFilter(pill.key)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all duration-150 ${
                    isActive
                      ? 'bg-teal-600 text-white shadow-sm'
                      : 'bg-slate-50 text-slate-500 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  {pill.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Master-Detail Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Table (2/3) */}
        <div className="lg:col-span-2">
          {loading ? (
            <TableSkeleton rows={8} cols={6} />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No drivers found"
              description={
                search || statusFilter
                  ? 'Try adjusting your search or filters'
                  : canManage
                    ? 'Add your first driver to get started'
                    : 'No drivers have been set up yet'
              }
              action={canManage && !search && !statusFilter ? 'Add Driver' : undefined}
              onAction={
                canManage && !search && !statusFilter
                  ? () => {
                      setEditing(null);
                      setShowModal(true);
                    }
                  : undefined
              }
            />
          ) : (
            <div className="bg-surface rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/60">
                      {['Name', 'License', 'Haulier', 'Contact', 'Status', 'Whitelist', ''].map(
                        (h) => (
                          <th
                            key={h}
                            className="text-left px-4 py-3 text-[11px] font-semibold text-slate-400 uppercase tracking-widest whitespace-nowrap"
                          >
                            {h}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filtered.map((d) => {
                      const isSelected = selected?.driver_id === d.driver_id;
                      return (
                        <tr
                          key={d.driver_id}
                          onClick={() => handleSelectRow(d)}
                          className={`cursor-pointer transition-colors duration-100 ${
                            isSelected
                              ? 'bg-teal-50/50 border-l-2 border-l-teal-500'
                              : 'hover:bg-slate-50/70 border-l-2 border-l-transparent'
                          }`}
                        >
                          <td className="px-4 py-3">
                            <span className="text-sm font-semibold text-slate-800">
                              {d.driver_name}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-slate-600" style={MONO}>
                              {d.license_number || '\u2014'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {d.hauler_name || '\u2014'}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-500">
                            {d.contact_number || '\u2014'}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge status={d.status} />
                          </td>
                          <td className="px-4 py-3">
                            {canManage ? (
                              <WhitelistToggle
                                enabled={d.whitelisted}
                                onClick={(e) => handleWhitelistToggle(e, d)}
                              />
                            ) : (
                              <span
                                className={`text-xs font-medium ${d.whitelisted ? 'text-teal-700' : 'text-slate-500'}`}
                              >
                                {d.whitelisted ? 'Yes' : 'No'}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {canManage && (
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditing(d);
                                    setShowModal(true);
                                  }}
                                  className="p-1.5 hover:bg-teal-50 rounded-lg text-slate-400 hover:text-teal-600 transition-colors"
                                  title="Edit driver"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleting(d);
                                  }}
                                  className="p-1.5 hover:bg-red-50 rounded-lg text-slate-400 hover:text-red-500 transition-colors"
                                  title="Delete driver"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Detail Panel (1/3) */}
        <div className="lg:col-span-1">
          {selected ? (
            <div className="bg-surface rounded-xl border border-slate-200 sticky top-6 overflow-hidden">
              {/* Detail Header */}
              <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/40">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-slate-800 truncate pr-3">
                    {selected.driver_name}
                  </h3>
                  {selected.whitelisted ? (
                    <div
                      className="flex-shrink-0 w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center"
                      title="Whitelisted"
                    >
                      <Shield className="w-4.5 h-4.5 text-teal-600" />
                    </div>
                  ) : (
                    <div
                      className="flex-shrink-0 w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center"
                      title="Not whitelisted"
                    >
                      <ShieldOff className="w-4.5 h-4.5 text-slate-400" />
                    </div>
                  )}
                </div>
              </div>

              {/* Detail Body */}
              <div className="p-5 space-y-4">
                <div>
                  <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                    License
                  </span>
                  <p className="text-sm text-slate-700 mt-0.5" style={MONO}>
                    {selected.license_number || 'Not provided'}
                  </p>
                </div>

                <div>
                  <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                    Contact
                  </span>
                  <p className="text-sm text-slate-700 mt-0.5">
                    {selected.contact_number || 'Not provided'}
                  </p>
                </div>

                <div>
                  <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                    Haulier
                  </span>
                  <p className="text-sm text-slate-700 mt-0.5">
                    {selected.hauler_name || 'None assigned'}
                  </p>
                </div>

                <div>
                  <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                    Status
                  </span>
                  <div className="mt-1.5">
                    <StatusBadge status={selected.status} />
                  </div>
                </div>

                {selected.notes && (
                  <div>
                    <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                      Notes
                    </span>
                    <p className="text-sm text-slate-600 mt-0.5 leading-relaxed">
                      {selected.notes}
                    </p>
                  </div>
                )}

                {/* Assigned Vehicles */}
                {selected.vehicles && selected.vehicles.length > 0 && (
                  <div className="pt-4 border-t border-slate-100">
                    <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                      Assigned Vehicles ({selected.vehicles.length})
                    </span>
                    <div className="mt-2.5 space-y-2">
                      {selected.vehicles.map((v) => (
                        <div
                          key={v.vehicle_id}
                          className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2.5 border border-slate-100"
                        >
                          <div className="flex items-center gap-2.5">
                            <Truck className="w-4 h-4 text-slate-400" />
                            <span className="text-sm text-slate-700 font-medium" style={MONO}>
                              {v.plate_number}
                            </span>
                          </div>
                          {v.is_primary && (
                            <span className="text-[9px] bg-teal-50 text-teal-600 border border-teal-200 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                              Primary
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Created Timestamp */}
                <div className="pt-4 border-t border-slate-100">
                  <p className="text-[10px] text-slate-400" style={MONO}>
                    Created:{' '}
                    {selected.created_date_time
                      ? new Date(selected.created_date_time).toLocaleString()
                      : '\u2014'}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="hidden lg:block bg-surface rounded-xl border border-slate-200 p-12 text-center sticky top-6">
              <div className="w-14 h-14 rounded-xl bg-slate-50 flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-slate-300" />
              </div>
              <p className="text-sm font-medium text-slate-400">Select a driver to view details</p>
              <p className="text-xs text-slate-300 mt-1">Click any row in the table</p>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showModal && (
        <DriverModal
          driver={editing}
          companies={companies}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false);
            setEditing(null);
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          message={`Are you sure you want to delete driver "${deleting.driver_name}"? This action cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleting(null)}
        />
      )}
    </div>
  );
}
